import { type Dispatch, type SetStateAction, useMemo, useState } from 'react'

import { getDiffBetweenDates } from 'commons/utils/date'
import { Alert, DatePicker, Icon, Radio, SlimButton } from 'components'
import dayjs from 'dayjs'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { Controller, useForm, useFormContext } from 'react-hook-form'
import {
  getAnalysisReport,
  getPendingAnalysisReport,
} from 'store/ExtraVolumeContext/ExtraVolumeSlice'
import { useDispatch } from 'store/store'

import * as S from './styles'

type ReportModalProps = {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
}

type ReportKind = 'pending' | 'history'

// TODO: replace direct dayjs() usage with utils

const ModalReport = ({ open, setOpen }: ReportModalProps) => {
  const dispatch = useDispatch()
  const [selectedReport, setSelectedReport] = useState<ReportKind>('pending')
  const defaultValues = {
    beginDate: dayjs().subtract(1, 'year').format('YYYY-MM'),
    endDate: dayjs().format('YYYY-MM'),
  }

  const { getValues: getFilterValues } = useFormContext()

  const { control, trigger, handleSubmit, getValues, reset, formState } = useForm({
    defaultValues,
    mode: 'onChange',
  })

  const handleClose = () => {
    setOpen(false)
    setSelectedReport('pending')
    reset(defaultValues)
  }

  const submitForm = handleSubmit((data) => {
    if (selectedReport === 'pending') {
      const {
        EquipeVenda,
        EscritorioVenda,
        Materias,
        Mercado,
        MesesCota,
        RegiaoVenda,
        TipoCritica,
      } = getFilterValues()
      dispatch(
        getPendingAnalysisReport({
          EquipeVenda,
          EscritorioVenda,
          Materias,
          Mercado,
          MesesCota,
          RegiaoVenda,
          TipoCritica,
        }),
      )
    } else {
      const payload = {
        dt_fim: dayjs(data.endDate).endOf('month').format('YYYY-MM-DD HH:mm:ss'),
        dt_ini: dayjs(data.beginDate).format('YYYY-MM-DD HH:mm:ss'),
      }
      dispatch(getAnalysisReport(payload))
    }
    handleClose()
  })

  const isValidInterval = useMemo(() => {
    const { beginDate, endDate } = getValues()
    if (formState.errors.beginDate || formState.errors.endDate) return true
    return getDiffBetweenDates(beginDate, endDate, 'month') <= 12
  }, [formState])

  return (
    <>
      {open && (
        <ModalContentLayout closeAction={handleClose} title="Baixar relatório" titleSize={24}>
          <S.Content>
            <S.RadioGroup>
              <Radio
                checked={selectedReport === 'pending'}
                label="Análises pendentes"
                name="report_type"
                onChange={() => {
                  setSelectedReport('pending')
                  reset(defaultValues)
                }}
                value="pending"
              />
              <Radio
                checked={selectedReport === 'history'}
                label="Histórico"
                name="report_type"
                onChange={() => setSelectedReport('history')}
                value="history"
              />
            </S.RadioGroup>

            {selectedReport === 'history' && (
              <>
                <S.Divider />
                <span>Informe um período de até 1 ano:</span>
                <S.FieldsWrapper>
                  <Controller
                    control={control}
                    name="beginDate"
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        disableMaskedInput={false}
                        error={fieldState.error?.message}
                        errorState={!isValidInterval}
                        height={30}
                        inputFormat="MM/YYYY"
                        label="Mês cota inicial"
                        maxDate={dayjs().add(2, 'year') as unknown as string}
                        minDate={dayjs().subtract(1, 'year') as unknown as string}
                        onChange={(date) => {
                          field.onChange(dayjs(date).format('YYYY-MM'))
                          trigger()
                        }}
                        views={['year', 'month']}
                      />
                    )}
                    rules={{
                      validate: {
                        min: (date) =>
                          dayjs(date)
                            .endOf('month')
                            .isAfter(dayjs().subtract(1, 'year').startOf('month')) ||
                          'Data inválida.',
                      },
                    }}
                  />
                  <Controller
                    control={control}
                    name="endDate"
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        disableMaskedInput={false}
                        error={fieldState.error?.message}
                        errorState={!isValidInterval}
                        height={30}
                        inputFormat="MM/YYYY"
                        label="Mês cota final"
                        maxDate={dayjs().add(2, 'year') as unknown as string}
                        minDate={dayjs().subtract(1, 'year') as unknown as string}
                        onChange={(date) => {
                          field.onChange(dayjs(date).format('YYYY-MM'))
                          trigger()
                        }}
                        views={['year', 'month']}
                      />
                    )}
                    rules={{
                      validate: {
                        afterBegin: (date) =>
                          dayjs(date)
                            .endOf('month')
                            .isAfter(dayjs(getValues('beginDate')).startOf('month')) ||
                          'Período inválido.',
                        min: (date) =>
                          dayjs(date)
                            .endOf('month')
                            .isAfter(dayjs().subtract(1, 'year').startOf('month')) ||
                          'Data inválida.',
                      },
                    }}
                  />
                </S.FieldsWrapper>

                {!isValidInterval && (
                  <Alert fullWidth kind="error" title="O período deve ser inferior a 1 ano" />
                )}
              </>
            )}
          </S.Content>

          <S.Footer>
            <S.ButtonWrapper>
              <SlimButton
                content="Voltar"
                icon={<Icon icon="undo" width="15" />}
                onClick={handleClose}
                variant="secondary"
              />
            </S.ButtonWrapper>
            <S.ButtonWrapper>
              <SlimButton
                content="Baixar"
                disabled={selectedReport !== 'pending' && (!isValidInterval || !formState.isValid)}
                icon={<Icon icon="download" width="15" />}
                onClick={submitForm}
                variant="primary"
              />
            </S.ButtonWrapper>
          </S.Footer>
        </ModalContentLayout>
      )}
    </>
  )
}

export default ModalReport
